import { useEffect, useState, useCallback } from 'react';
import type { Loan } from '@/types';
import { getLoan } from '@/lib/db';

export interface UseLoanResult {
  loan: Loan | undefined;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Loads the single primary Loan record from IndexedDB and exposes a
 * refresh() callback for after mutations (create/update). Deliberately
 * simple (poll-on-demand via refresh, not a live Dexie liveQuery
 * subscription) to keep Phase 1's dependency surface minimal; a
 * liveQuery-based version can replace this transparently later if
 * needed since the hook's return shape would stay the same.
 */
export function useLoan(): UseLoanResult {
  const [loan, setLoan] = useState<Loan | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getLoan();
      setLoan(result);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { loan, isLoading, refresh };
}
